import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { JobTypePerformanceChart } from "@app/operations/project-performance/JobTypePerformanceChart";
import { ProjectPerformanceFilters } from "@app/operations/project-performance/ProjectPerformanceFilters";
import { ProjectPerformanceService } from "@app/operations/project-performance/projectPerformanceService";
import { TotalRuntimeChart } from "@app/operations/project-performance/TotalRuntimeChart";

const ProjectPerformancePage: React.FC = () => {
  const projectPerformanceService = useRef(new ProjectPerformanceService()).current;

  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>();
  const [visibleJobTypes, setVisibleJobTypes] = useState<string[]>([]);

  const { data: runData = [], isError, isFetching } = useQuery({
    enabled: !!selectedProjectId,
    queryFn: ({ signal }) => projectPerformanceService.getProjectRunData(selectedProjectId!, signal),
    queryKey: ["project-performance", "run-data", selectedProjectId],
  });

  useEffect(() => {
    if (isError) {
      toast(<span>Failed to load project performance data.</span>, { containerId: "z-index-9999", type: "error" });
    }
  }, [isError]);

  const availableJobTypes = useMemo(
    () => Array.from(new Set(runData.flatMap(run => run.jobTypeBreakdown.map(jobTypeStats => jobTypeStats.jobType)))),
    [runData]
  );

  return (
    <Page documentTitle="Project Performance">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Project Performance"]} />
      <PageInner>
        <PageHeader title="Project Performance" disableHR={false} />
        <PageSection>
          <div className="container mt-3">
            <ProjectPerformanceFilters
              availableJobTypes={availableJobTypes}
              isLoading={isFetching}
              onJobTypesChanged={setVisibleJobTypes}
              onProjectSelected={setSelectedProjectId}
            />
            {!selectedProjectId && (
              <div className="alert alert-info">Select a client and project to view performance data.</div>
            )}
            {!!selectedProjectId && !isFetching && (
              <>
                <TotalRuntimeChart data={runData} />
                <JobTypePerformanceChart data={runData} visibleJobTypes={visibleJobTypes} />
              </>
            )}
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
};

export default ProjectPerformancePage;
